import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { useIntl } from 'react-intl-next';
import { getActivatedJiraSearchPlaceholderMessage, messages } from '../../../messages';
import { SearchDialogProduct, useProductContext } from '../../product-router';
import { useDefaultSuppliers } from '../../result-supplier';
import { JIRA_PRODUCT_ID } from './types';
import { useGenericFilters } from '../../filters';
import { useFilters } from './filters';
import { useSections } from './sections';
import { useURLFactory } from './url-factory';
import { JiraAdvancedSearch } from './advanced-search';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { onAdvancedSearchSelected } from '../../../common/analytics/events';
import { useSearchConfigContext } from '../../../common/search-config-provider';
import { defaultPermissionsSupplier } from './permission-supplier';
import { useDialogExpansionContext } from '../../dialog-expansion-context';
import { useQuery } from '../../query-context';
import { appendQueryParams } from './jira-analytics-query-params';
import SingleSiteAdvancedSearch from './single-site-advanced-search';
import { ProductStates } from '../../product-state-machine';
import { LoadingSpinner } from '../../../common/loading-spinner';
import { useABTest } from '../../../extensible/ab-test-context/ab-test-context';
import { NoPreQueryResults } from '../../no-results-screen';
import { useJiraDateFilter } from './filter-by-date/use-jira-date-filter';
let hasLoadedFilters = false;
let currentProjectRef;
let currentUserRef;
export const setHasLoadedFilters = value => hasLoadedFilters = value;
const JiraNoPreQueryResults = () => /*#__PURE__*/React.createElement(NoPreQueryResults, {
  message: messages.jira_empty_state_prequery_heading
});
export const ExtensibleJiraTab = ({
  features,
  children,
  config,
  formatDate,
  autocorrectEnabled,
  currentProject,
  currentUser,
  preQueryItemSupplier,
  postQueryItemSupplier,
  ...rest
}) => {
  var _currentProjectRef, _currentUserRef, _currentUserRef2, _getActiveProduct;
  const {
    formatMessage
  } = useIntl();
  const {
    jiraSites
  } = useSearchConfigContext();
  const {
    isExpanded
  } = useDialogExpansionContext();
  const {
    getActiveProduct
  } = useProductContext();
  const {
    query
  } = useQuery();
  const hasAdvancedRoadmapsAccess = !!(features !== null && features !== void 0 && features.hasAdvancedRoadmapsAccess);
  const hasSoftwareAccess = !!(features !== null && features !== void 0 && features.hasSoftwareAccess);
  const isPreviewOnJiraSearchResultsEnabled = !!(features !== null && features !== void 0 && features.isPreviewOnJiraSearchResultsEnabled);
  const isReportedByMeFilterEnabled = !!(features !== null && features !== void 0 && features.isReportedByMeFilterEnabled);
  const isConsistentIssueDetailsEnabled = !!(features !== null && features !== void 0 && features.isConsistentIssueDetailsEnabled);
  const isDateFromFilterEnabled = !!(features !== null && features !== void 0 && features.enableDateFromFilter);
  const shouldShowIssueLabelsFilter = !!(features !== null && features !== void 0 && features.enableIssueLabelsFilter);
  const isPreQueryFiltersEnabled = !!(features !== null && features !== void 0 && features.isPreQueryFiltersEnabled);
  const {
    defineFilters
  } = useGenericFilters();
  const {
    filtersDefinition,
    loadFilters,
    filterValueGetters
  } = useFilters(rest, shouldShowIssueLabelsFilter, config, currentProject, currentUser, isReportedByMeFilterEnabled);
  const {
    jiraTabDateFilterDefinition,
    appliedDateFilterValue
  } = useJiraDateFilter({
    isDateFromFilterEnabled
  });
  const abTest = useABTest();
  const isFasterSearchDisabled = abTest !== null && (abTest.experimentId === 'durian-without-faster-search' || abTest.experimentId === 'default-without-faster-search');
  if ((!hasLoadedFilters || ((_currentProjectRef = currentProjectRef) === null || _currentProjectRef === void 0 ? void 0 : _currentProjectRef.id) !== (currentProject === null || currentProject === void 0 ? void 0 : currentProject.id) || ((_currentUserRef = currentUserRef) === null || _currentUserRef === void 0 ? void 0 : _currentUserRef.id) !== (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) || ((_currentUserRef2 = currentUserRef) === null || _currentUserRef2 === void 0 ? void 0 : _currentUserRef2.avatarUrl) !== (currentUser === null || currentUser === void 0 ? void 0 : currentUser.avatarUrl)) && isExpanded && ((_getActiveProduct = getActiveProduct()) === null || _getActiveProduct === void 0 ? void 0 : _getActiveProduct.id) === JIRA_PRODUCT_ID && (isPreQueryFiltersEnabled || query !== '')) {
    loadFilters();
    currentProjectRef = currentProject;
    currentUserRef = currentUser;
    setHasLoadedFilters(true);
  }
  defineFilters(JIRA_PRODUCT_ID, [...(isDateFromFilterEnabled ? jiraTabDateFilterDefinition : []), ...filtersDefinition]);
  const {
    getFiltersAdvancedSearchUrl
  } = useURLFactory(filterValueGetters, appliedDateFilterValue, isPreQueryFiltersEnabled);
  const {
    recentSectionTitleGenerator,
    sections
  } = useSections(hasAdvancedRoadmapsAccess, hasSoftwareAccess, getFiltersAdvancedSearchUrl, isPreviewOnJiraSearchResultsEnabled, useIntl(), formatDate, isConsistentIssueDetailsEnabled, autocorrectEnabled);
  const itemSuppliers = useDefaultSuppliers(JIRA_PRODUCT_ID, sections, {
    preQueryItemSupplier,
    postQueryItemSupplier
  });
  return /*#__PURE__*/React.createElement(SearchDialogProduct, _extends({
    id: JIRA_PRODUCT_ID,
    expandedStateInputPlaceholder: formatMessage(getActivatedJiraSearchPlaceholderMessage(hasSoftwareAccess, hasAdvancedRoadmapsAccess)),
    title: formatMessage(messages.jira_tab_label),
    autocorrectEnabled: autocorrectEnabled,
    sections: sections,
    preQuerySectionTitleGenerator: recentSectionTitleGenerator,
    generateAdvancedSearchUrl: getFiltersAdvancedSearchUrl
  }, rest, itemSuppliers, {
    permissionSupplier: jiraSites.length > 0 ? defaultPermissionsSupplier : rest.permissionSupplier,
    appendQueryParams: appendQueryParams,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled,
    debounceTime: getBooleanFF('platform.jira-quick-search-debounce-optimisation_m4dm4') ? 300 : undefined
    // When it's undefined, it defaults to 250ms
  }), ({
    linkComponent,
    productState
  }) => {
    return {
      Body: () => {
        if (productState === ProductStates.PostQueryLoading && isFasterSearchDisabled) {
          return /*#__PURE__*/React.createElement(LoadingSpinner, null);
        } else if (productState === ProductStates.PreQueryNoResult) {
          return /*#__PURE__*/React.createElement(JiraNoPreQueryResults, null);
        }
      },
      SectionFooter: jiraSites.length > 1 ? undefined // don't show the footer for multi sites
      : () => /*#__PURE__*/React.createElement(SingleSiteAdvancedSearch, {
        advancedSearchSelected: onAdvancedSearchSelected,
        linkComponent: linkComponent,
        productState: productState,
        features: features
      }),
      Footer: () => /*#__PURE__*/React.createElement(JiraAdvancedSearch, {
        linkComponent: linkComponent,
        productState: productState,
        features: features
      })
    };
  });
};